import { jsx } from 'react/jsx-runtime';
import { createContext, useMemo, useEffect, useRef, useCallback } from 'react';
export { AnnouncementPrimitive as Message } from '../announcement-primitive/announcement-primitive.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { isAnyModifierPressed } from '../utils/modifiers.js';

/**
 * The root container for an unstyled, "headless" Toast component (ToastPrimitive).
 * @see https://fpl.figma.design/components/primitives/toast/
 */
function Root({ children, className, onClose, timeout, ...props }) {
    const autoCloseProps = useAutoClose(timeout, onClose);
    const context = useMemo(() => {
        return {
            subscriberCount: 0,
            signalClose: (reason) => onClose?.(reason),
        };
    }, [onClose]);
    return (jsx(ToastContext.Provider, { value: context, children: jsx("div", { "data-fpl-component": "primitive", ...props, role: "presentation", onClick: () => {
                if (context.subscriberCount === 0) {
                    onClose?.('dismiss');
                }
            }, onKeyDown: (e) => {
                if (isAnyModifierPressed(e))
                    return;
                if (e.key === 'Escape') {
                    onClose?.('dismiss');
                }
            }, ...autoCloseProps, children: jsx("div", { className: className, role: "group", children: children }) }) }));
}
Root.displayName = 'ToastPrimitive.Root';
const ToastContext = createContext(undefined);
/**
 * Allows children of a toast to dismiss it - e.g. in a close button.
 */
function useDismissToast() {
    const toast = useValidatedContext(ToastContext, 'useDismissToast', 'ToastPrimitive.Root');
    useEffect(() => {
        ++toast.subscriberCount;
        return () => {
            --toast.subscriberCount;
        };
    }, [toast]);
    return (reason) => toast.signalClose(reason);
}
/** Encapsulates auto-close behavior, which closes after a timeout but avoids closing while the user is attempting to interact (eg, has keyboard focus within the closeable element) */
function useAutoClose(timeout, onClose) {
    const elementRef = useRef(null);
    const timedOutRef = useRef(false);
    const callbackRef = useRef(onClose);
    callbackRef.current = onClose;
    const checkConditionsAndFire = useCallback(() => {
        const timedOut = timedOutRef.current;
        const hasFocusOrPointer = elementRef.current?.matches(':focus-within');
        if (timedOut && !hasFocusOrPointer) {
            callbackRef.current?.apply(undefined, ['timeout']);
        }
    }, []);
    useEffect(() => {
        timedOutRef.current = false;
    }, [timeout]);
    useEffect(() => {
        let timerHandle;
        // Setting Infinity as the timeout will actually trigger the callback _immediately_, which is
        // the opposite of what we want. See: https://github.com/denysdovhan/wtfjs/issues/61
        if (timeout !== undefined && timeout !== Infinity) {
            timerHandle = window.setTimeout(() => {
                timedOutRef.current = true;
                checkConditionsAndFire();
            }, timeout);
        }
        return () => window.clearTimeout(timerHandle);
    }, [checkConditionsAndFire, timeout]);
    return {
        ref: elementRef,
        onBlur: checkConditionsAndFire,
    };
}

export { Root, useDismissToast };
//# sourceMappingURL=toast-primitive.js.map
